import React, { useCallback, useEffect, useState } from 'react';
import { Button, Divider, message, Spin } from 'antd';
import { useGlobalState } from '../GlobalState';
import { formatDate, formatDateWithTime } from '../../../../utils/date';
import axios from 'axios';
import { USER_BOOKING_STEP_ORDER } from '../../index';
import moment from 'moment';
import Moment from 'moment';
import InfoIcon from '../../../../assets/images/Icons/SVG/InfoIcon';
import CalendarIcon from '../../../../assets/images/Icons/SVG/CalendarIcon';
import SummaryFisheryMasthead from './SummaryFisheryMasthead';
import { exotic_fishery } from '../../../common/Utils';
import { LoadingOutlined } from '@ant-design/icons';
import { notification } from '../../../../utils/messaging';
import {
  useGetDynamicBookingTextQuery,
  useGetUserHasSubscriptionQuery,
  useGetUserWalletQuery,
  useReevaluateEngagementMutation,
} from '../../../user_profile/userProfileSlice';
import UserBookingPaymentTotal from './UserBookingPaymentTotal';
import { apiSlice } from '../../../../store/apiSlice';
import { useDispatch } from 'react-redux';
import { getSummaryCosts } from '../../../../utils/cost';
import BoxIcon from '../../../../assets/images/Icons/SVG/BoxIcon';
import SummaryDivider from './SummaryDivider';

const handleNewBookingError = (err) => {
  if (err.response) {
    if (err.response.status === 401 || err.response.status === 422) {
      if (err.response.data.detail[0].msg && err.response.status === 422) {
        notification.error({
          description: err.response.data.detail[0].msg,
        });
      }
    } else if (err.response.status === 400 || err.response.status === 423) {
      notification.error({
        description: err.response.data.detail,
      });
    } else {
    }
  } else {
    notification.error({
      description: 'Error processing booking result',
    });
  }
};

const spinIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

export function nameListToString(names) {
  if (!names?.length) {
    return '';
  }
  if (names.length === 1) {
    return names[0];
  }
  return names.slice(0, -1).join(', ') + ' & ' + names.at(-1);
}

function UserBookingSummary({ fisheryPublicId }) {
  const is_exotic_fishery = fisheryPublicId === exotic_fishery;
  const { data: DynamicText } = useGetDynamicBookingTextQuery(fisheryPublicId);
  const dispatch = useDispatch();
  const [current, setCurrent] = useGlobalState('current');
  const [, setPaymentFormData] = useGlobalState('paymentFormData');
  const [, setReservationExpiryTime] = useGlobalState('reservationExpiryTime');
  const [userSelectedAnglers] = useGlobalState('userSelectedAnglers');
  const [userSelectedLakeName] = useGlobalState('userSelectedLakeName');
  const [userSelectedRods] = useGlobalState('userSelectedRods');
  const [estimatedPricing, setEstimatedPricing] =
    useGlobalState('estimatedPricing');
  const [, setFinalPricing] = useGlobalState('finalPricing');
  const [, setStoredCards] = useGlobalState('storedCards');
  const [, setStoredCardsError] = useGlobalState('storedCardsError');
  const [arrivalTime] = useGlobalState('arrivalTime');
  const [departureTime] = useGlobalState('departureTime');
  const [userSelectedSwims] = useGlobalState('userSelectedSwims');
  const [userSelectedDoubleSwim] = useGlobalState('userSelectedDoubleSwim');
  const [fisheryInfo] = useGlobalState('fisheryInfo');
  const [userSelectedLakeId] = useGlobalState('userSelectedLakeId');
  const [lakeExclusive] = useGlobalState('lakeExclusive');
  const [lakeDetails] = useGlobalState('lakeDetails');
  const [swimAvailableData] = useGlobalState('swimAvailableData');
  const [userBookingData] = useGlobalState('userBookingData');
  const [, setBookingId] = useGlobalState('bookingId');
  const [, setBookingUserId] = useGlobalState('bookingUserId');
  const [, setPaymentError] = useGlobalState('paymentError');
  const [, setPaymentSuccessRedirect] = useGlobalState(
    'paymentSuccessRedirect'
  );
  const [bookingType] = useGlobalState('bookingType');
  const [addedProductItems] = useGlobalState('addedProductItem');
  const [userSelectedPakage] = useGlobalState('userSelectedPakage');
  const [accommodation] = useGlobalState('accommodation');
  const [isArrivalFixed, setIsArrivalFixed] = useGlobalState('isArrivalFixed');
  const [isDepartureFixed, setIsDepartureFixed] =
    useGlobalState('isDepartureFixed');
  const [customPropertyDetails, setCustomPropertyDetails] = useGlobalState(
    'customPropertiesDetails'
  );
  const [triggerEngagementReevaluation] = useReevaluateEngagementMutation();
  const { data: { is_active: isWalletActive } = { is_active: false } } =
    useGetUserWalletQuery();
  const { data: userHasSubscription } = useGetUserHasSubscriptionQuery();
  const walletEnabled = userHasSubscription && isWalletActive;
  const is_exotic_arapaima_lake =
    userSelectedLakeId === 'a4688274-3f67-40a5-be8d-a8dc51c053b3';
  const cost = getSummaryCosts(
    lakeExclusive,
    is_exotic_fishery && !is_exotic_arapaima_lake,
    estimatedPricing
  );

  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [loadingFinalPriceEstimate, setLoadingFinalPriceEstimate] =
    useState(true);
  const [usingWalletAmountPence, setUsingWalletAmountPence] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadFinalPricing = useCallback(() => {
    let cost_payload = {
      lake_id: userSelectedLakeId,
      arrival_timestamp: arrivalTime,
      departure_timestamp: departureTime,
      num_anglers: userSelectedAnglers,
      num_guest: userSelectedRods,
      swim_ids: userSelectedSwims.concat(userSelectedDoubleSwim),
      angler_details: userBookingData.angler_details,
      booking_type: lakeExclusive ? 'lake_exclusive' : bookingType,
      apply_promotion: true,
      add_on_details: addedProductItems,
      ...(userSelectedPakage && {
        custom_slot_id:
          lakeDetails?.custom_slot_days[userSelectedPakage - 1]?.id,
      }),
    };

    const axios_config = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    };

    axios
      .post('booking/lake/booking/cost', cost_payload, axios_config)
      .then((res) => {
        if (res.status === 200) {
          setEstimatedPricing(res.data.result);
          setLoadingFinalPriceEstimate(false);
        }
      })
      .catch((err) => {
        handleNewBookingError(err);
        setBookingInProgress(false);
      });
  }, [
    addedProductItems,
    arrivalTime,
    bookingType,
    departureTime,
    lakeDetails?.custom_slot_days,
    lakeExclusive,
    setEstimatedPricing,
    userBookingData.angler_details,
    userSelectedAnglers,
    userSelectedDoubleSwim,
    userSelectedLakeId,
    userSelectedPakage,
    userSelectedRods,
    userSelectedSwims,
  ]);

  useEffect(() => {
    loadFinalPricing();
  }, [loadFinalPricing]);

  const labelClass = 'gSemiBold';
  const summaryFieldClass = 'fs-18';

  const back = () => {
    setCurrent(current - 1);
  };

  const createBooking = () => {
    setBookingInProgress(true);
    setReservationExpiryTime(null);

    let payload_data = {
      lake_id: userSelectedLakeId,
      arrival_timestamp: arrivalTime,
      departure_timestamp: departureTime,
      anglers: userSelectedAnglers,
      guest: userSelectedRods,
      swims: userSelectedSwims.concat(userSelectedDoubleSwim),
      amount: estimatedPricing?.amount,
      details: userBookingData,
      notes: userBookingData.notes,
      vehicle_registration_number: userBookingData?.vehicle_registration_number,
      custom_properties: customPropertyDetails,
      book_lake_exclusive: lakeExclusive,
      booking_type: bookingType,
      use_wallet: walletEnabled
        ? Boolean(usingWalletAmountPence > 0)
        : undefined,
      wallet_amount_to_spend: walletEnabled
        ? (usingWalletAmountPence / 100).toFixed(2)
        : 0,
      add_on_details: addedProductItems,
      ...(userSelectedPakage && {
        custom_slot_id:
          lakeDetails?.custom_slot_days[userSelectedPakage - 1]?.id,
      }),
    };
    const axios_new_config = {
      baseURL: `${process.env.REACT_APP_AXIOS_URL}`,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    };

    axios
      .post('booking/new', payload_data, axios_new_config)
      .then((res) => {
        if (res.status === 200) {
          let _result = res.data.result;
          setBookingId({
            public_id: _result.public_id,
            booking_id: _result.booking_id,
          });
          setBookingUserId(_result?.user_id)
          if (_result?.stored_cards?.status) {
            if (_result?.stored_cards?.status === 200) {
              setStoredCards(_result?.stored_cards?.details || []);
              setStoredCardsError(null);
            } else {
              setStoredCardsError(
                'There was an error fetching your stored cards.'
              );
            }
          }
          const payableCost = lakeExclusive
            ? _result.booking_price?.advance_price
            : _result.booking_price?.total_price;
          setFinalPricing(payableCost);
          if (parseFloat(payableCost) > 0) {
            setReservationExpiryTime(Moment(moment.now()).add(15, 'minutes'));
            setPaymentFormData(null);
            setCurrent(USER_BOOKING_STEP_ORDER.indexOf('payment'));
          } else {
            const axios_config = {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              withCredentials: true,
            };
            axios
              .post(
                'payment/direct/confirm_free',
                {},
                {
                  ...axios_config,
                  params: { booking_public_id: _result.public_id },
                }
              )
              .then((res) => {
                if (res.status === 200) {
                  setPaymentSuccessRedirect(res?.data?.redirect);
                  setCurrent(USER_BOOKING_STEP_ORDER.indexOf('success'));
                  axios.post('webhooks/trigger_pending');
                  triggerEngagementReevaluation();
                  dispatch(apiSlice.util.invalidateTags(['CreditHistory']));
                }
              })
              .catch((err) => {
                setPaymentError(err?.response?.data?.detail);
                setCurrent(USER_BOOKING_STEP_ORDER.indexOf('failure'));
                setBookingInProgress(false);
              });
          }
        } else {
          message.error('Error processing result');
          setBookingInProgress(false);
        }
      })
      .catch((err) => {
        handleNewBookingError(err);
        setBookingInProgress(false);
      });
  };

  const numTickets = userSelectedAnglers;
  const numSwims =
    (userSelectedSwims?.length || 0) + (userSelectedDoubleSwim?.length || 0);
  const selectedSwimNames = (swimAvailableData?.swims || [])
    .filter((s) => userSelectedSwims?.includes(s.id))
    .map((s) => s.name);
  const hasAddOns = Boolean(addedProductItems?.length);

  return (
    <>
      <div
        className={
          'user-booking-summary__header sb-blue-bg-600 white gSemiBold txt-center'
        }
      >
        <span>Booking Summary</span>
        <span className={'user-booking-summary__header__logo'}>
          swimbooker&trade;
        </span>
      </div>
      <SummaryFisheryMasthead
        fisheryInfo={fisheryInfo}
        className={'user-booking-summary__masthead--top-padding'}
      />
      <div
        className={
          'txt-left gRegular fs-16 mg-right-25 mg-left-25 mg-top-10 pd-bottom-30'
        }
      >
        <SummaryDivider title={'Booking Details'} icon={<InfoIcon />} />
        {fisheryPublicId !== exotic_fishery && (
          <p className={summaryFieldClass}>
            {DynamicText?.is_active &&
            DynamicText?.properties?.booking_step_2?.booking_type ? (
              ''
            ) : (
              <span className={labelClass}>
                {accommodation ? 'Booked' : 'Lake'}:
              </span>
            )}{' '}
            {userSelectedLakeName}
          </p>
        )}
        {!accommodation && (
          <p className={summaryFieldClass}>
            <span className={labelClass}>
              {DynamicText?.is_active &&
              DynamicText?.properties?.booking_summary?.lake_text
                ? DynamicText?.properties?.booking_summary?.lake_text
                : 'Selected'}
              :
            </span>{' '}
            {DynamicText?.is_active &&
            DynamicText?.properties?.booking_summary?.lake_text
              ? lakeDetails?.custom_slot_days[userSelectedPakage - 1]?.name
              : lakeExclusive
                ? 'Lake Exclusive'
                : numSwims
                  ? nameListToString(selectedSwimNames)
                  : `${numTickets} Ticket${numTickets > 1 ? 's' : ''}`}
          </p>
        )}
        {(!lakeExclusive || lakeDetails?.lake_exclusive_anglers_allowed) &&
          !accommodation && (
            <p className={summaryFieldClass}>
              {DynamicText?.is_active ? (
                DynamicText?.properties?.booking_step_1?.lake_exclusive_label
              ) : (
                <>
                  <span className={labelClass}>
                    {lakeExclusive ? 'Max ' : ''}
                    {is_exotic_fishery && !is_exotic_arapaima_lake
                      ? 'No. of Guests:'
                      : 'No. of Anglers:'}
                  </span>{' '}
                  {lakeExclusive
                    ? lakeDetails.lake_exclusive_anglers_allowed
                    : userSelectedAnglers}
                </>
              )}
            </p>
          )}
        {!lakeExclusive && !accommodation && !is_exotic_arapaima_lake && (
          <p className={summaryFieldClass}>
            <span className={labelClass}>
              {fisheryPublicId !== exotic_fishery
                ? 'No. of Guests:'
                : 'No. of Anglers:'}
            </span>{' '}
            {userSelectedRods}
          </p>
        )}
        <SummaryDivider title={'Dates & Times'} icon={<CalendarIcon />} />
        <p className={summaryFieldClass}>
          <span className={labelClass}>Arrival Details:</span>{' '}
          {isArrivalFixed
            ? `${formatDate(arrivalTime)} ${isArrivalFixed}`
            : formatDateWithTime(arrivalTime)}
        </p>
        <p className={summaryFieldClass}>
          <span className={labelClass}>Departure Details:</span>{' '}
          {isDepartureFixed
            ? `${formatDate(departureTime)} ${isDepartureFixed}`
            : formatDateWithTime(departureTime)}
        </p>
        {hasAddOns && (
          <>
            <SummaryDivider title={'Add-Ons'} icon={<BoxIcon />} />
            {addedProductItems
              .filter(({ quantity }) => quantity)
              .map(({ product_id, name, quantity }) => (
                <p key={product_id} className={summaryFieldClass}>
                  <span className={labelClass}>{quantity}x</span> {name}
                </p>
              ))}
          </>
        )}
        <Divider />
        <UserBookingPaymentTotal
          loading={loadingFinalPriceEstimate}
          cost={cost}
          walletEnabled={walletEnabled}
          usingWalletAmountPence={usingWalletAmountPence}
          setUsingWalletAmountPence={setUsingWalletAmountPence}
        />

        {bookingInProgress ? (
          <div className={' txt-center w-full mg-top-20 mg-bottom-20'}>
            <Spin indicator={spinIcon} />
          </div>
        ) : (
          <Button
            className={
              (bookingInProgress || loadingFinalPriceEstimate
                ? ''
                : 'light-green-bg white') +
              ' txt-center br5 gSemiBold fs-20 w-full pd-10 h-full mg-top-10'
            }
            onClick={() => createBooking()}
            disabled={bookingInProgress || loadingFinalPriceEstimate}
          >
            {cost.payable > 0 ? 'Proceed to Payment' : 'Book Now'}
          </Button>
        )}
        <div className={'w-full txt-center'}>
          <Button className={'gSemiBold mg-top-15'} type={'text'}>
            <span className={'txt-underline fs-16'} onClick={back}>
              Edit Booking
            </span>
          </Button>
        </div>
      </div>
    </>
  );
}

export default UserBookingSummary;
